import { useState, useEffect, useCallback, forwardRef } from 'react';
import _isEmpty from 'lodash/isEmpty';
import _isNil from 'lodash/isNil';
import { FaPencilAlt } from 'react-icons/fa';
import { MdDelete } from 'react-icons/md';

import { Modal, ModalMethods, Text, Loader, toasts } from '../../../../ui-component';
import { api } from '../../../../utils/apiRequest';
import { getValueFromLocalStorage } from '../../../../utils/common';

export type ReadymadeUnit = {
  id: number;
  name: string;
  boutique_id?: number | null;
};

type ReadymadeUnitsModalProps = {
  selectedUnit: ReadymadeUnit | null;
  onSelect: (unit: ReadymadeUnit | null) => void;
};

const ReadymadeUnitsModal = forwardRef<ModalMethods, ReadymadeUnitsModalProps>(
  ({ selectedUnit, onSelect }, ref) => {
    const [units, setUnits] = useState<ReadymadeUnit[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isCreating, setIsCreating] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');

    const fetchUnits = useCallback(async () => {
      const boutiqueId = getValueFromLocalStorage('boutique_id');

      if (!boutiqueId) {
        toasts('error', 'Boutique ID not found', 'readymade-units-boutique-id-missing');

        return;
      }

      setIsLoading(true);

      try {
        const response = await api.getRequest('readymade/units');

        if (response.status && !_isNil(response.data)) {
          const data = Array.isArray(response.data) ? response.data : [];
          const valid = data.filter(
            (item: ReadymadeUnit) => item.name && !_isEmpty(item.name.trim())
          );

          setUnits(valid);
        } else {
          toasts(
            'error',
            response.message || 'Failed to fetch units',
            'readymade-units-load-error'
          );
          setUnits([]);
        }
      } catch (err) {
        if (err instanceof Error) {
          toasts('error', err.message, 'readymade-units-load-error');
        }
        setUnits([]);
      } finally {
        setIsLoading(false);
      }
    }, []);

    useEffect(() => {
      void fetchUnits();
    }, [fetchUnits]);

    const handleSelect = (unit: ReadymadeUnit) => {
      onSelect(unit);

      if (typeof ref === 'object' && ref !== null && 'current' in ref && ref.current) {
        ref.current.hide();
      }
    };

    const [editingUnit, setEditingUnit] = useState<ReadymadeUnit | null>(null);
    const [editingName, setEditingName] = useState('');
    const [isSavingEdit, setIsSavingEdit] = useState(false);
    const [deletingUnitId, setDeletingUnitId] = useState<number | null>(null);

    const handleCreate = async () => {
      const trimmed = searchQuery.trim();

      if (_isEmpty(trimmed)) {
        toasts('error', 'Please enter a unit name', 'readymade-unit-name-required');

        return;
      }

      const boutiqueId = getValueFromLocalStorage('boutique_id');

      if (!boutiqueId) {
        toasts('error', 'Boutique ID not found', 'readymade-units-boutique-id-missing');

        return;
      }

      setIsCreating(true);

      try {
        const encodedName = encodeURIComponent(trimmed);
        const response = await api.postRequest(`readymade/units?name=${encodedName}`, {});

        if (response.status && !_isNil(response.data)) {
          const unit = response.data as ReadymadeUnit;
          setUnits((prev) => [...prev, unit]);
          onSelect(unit);

          if (typeof ref === 'object' && ref !== null && 'current' in ref && ref.current) {
            ref.current.hide();
          }

          toasts('success', 'Unit created successfully', 'readymade-unit-create-success');
        } else {
          toasts(
            'error',
            response.message || 'Failed to create unit',
            'readymade-unit-create-error'
          );
        }
      } catch (err) {
        if (err instanceof Error) {
          toasts('error', err.message, 'readymade-unit-create-error');
        }
      } finally {
        setIsCreating(false);
      }
    };

    const startEdit = (unit: ReadymadeUnit) => {
      setEditingUnit(unit);
      setEditingName(unit.name);
    };

    const handleEditSave = async () => {
      if (!editingUnit) {
        return;
      }

      const trimmed = editingName.trim();

      if (_isEmpty(trimmed)) {
        toasts('error', 'Please enter a unit name', 'readymade-unit-name-required');

        return;
      }

      const boutiqueId = getValueFromLocalStorage('boutique_id');

      if (!boutiqueId) {
        toasts('error', 'Boutique ID not found', 'readymade-units-boutique-id-missing');

        return;
      }

      setIsSavingEdit(true);

      try {
        const encodedName = encodeURIComponent(trimmed);
        const response = await api.putRequest(
          `readymade/units/${editingUnit.id}?name=${encodedName}`,
          {}
        );

        if (response.status) {
          setUnits((prev) =>
            prev.map((unit) => (unit.id === editingUnit.id ? { ...unit, name: trimmed } : unit))
          );

          if (selectedUnit?.id === editingUnit.id) {
            onSelect({ ...editingUnit, name: trimmed });
          }

          setEditingUnit(null);
          setEditingName('');
          toasts('success', 'Unit updated successfully', 'readymade-unit-edit-success');
        } else {
          toasts('error', response.message || 'Failed to update unit', 'readymade-unit-edit-error');
        }
      } catch (err) {
        if (err instanceof Error) {
          toasts('error', err.message, 'readymade-unit-edit-error');
        }
      } finally {
        setIsSavingEdit(false);
      }
    };

    const handleDelete = async (unit: ReadymadeUnit) => {
      const boutiqueId = getValueFromLocalStorage('boutique_id');

      if (!boutiqueId) {
        toasts('error', 'Boutique ID not found', 'readymade-units-boutique-id-missing');

        return;
      }

      setDeletingUnitId(unit.id);

      try {
        const response = await api.deleteRequest(`readymade/units/${unit.id}`, {});

        if (response.status) {
          setUnits((prev) => prev.filter((item) => item.id !== unit.id));

          if (selectedUnit?.id === unit.id) {
            onSelect(null);
          }

          if (editingUnit?.id === unit.id) {
            setEditingUnit(null);
            setEditingName('');
          }

          toasts('success', 'Unit deleted successfully', 'readymade-unit-delete-success');
        } else {
          toasts(
            'error',
            response.message || 'Failed to delete unit',
            'readymade-unit-delete-error'
          );
        }
      } catch (err) {
        if (err instanceof Error) {
          toasts('error', err.message, 'readymade-unit-delete-error');
        }
      } finally {
        setDeletingUnitId(null);
      }
    };

    const filteredUnits = units.filter((unit) => {
      const q = searchQuery.trim().toLowerCase();

      if (_isEmpty(q)) {
        return true;
      }

      return unit.name.toLowerCase().includes(q);
    });

    const isQueryExisting = units.some(
      (unit) => unit.name.toLowerCase() === searchQuery.trim().toLowerCase()
    );

    const showCreate =
      !_isEmpty(searchQuery.trim()) && !isQueryExisting && !isLoading && !isCreating;

    return (
      <Modal ref={ref} size="medium" title="Select or add units" showFooter={false} showCloseIcon>
        <div className="flex flex-col h-full min-h-[360px]">
          <div className="mb-3">
            <Text className="font-inter text-sm text-gray-600 mb-2 mt-1 block" color="black">
              Choose how you measure inventory (Pieces, Sets, Kg, etc.).
            </Text>
            <input
              type="text"
              placeholder="Search or type a new unit"
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              className="w-full h-10 px-4 border border-gray-300 rounded-lg text-sm font-inter outline-none transition-colors focus:border-blue-500 focus:ring-2 focus:ring-blue-500 placeholder:text-gray-500"
            />
          </div>

          {isLoading ? (
            <div className="flex justify-center items-center h-56">
              <Loader />
            </div>
          ) : filteredUnits.length > 0 ? (
            <div className="flex flex-col gap-2 py-2 max-h-[220px] overflow-y-auto overflow-x-hidden">
              {filteredUnits.map((unit) => {
                const isSelected = selectedUnit?.id === unit.id;
                const canEditDelete = unit.boutique_id !== null && unit.boutique_id !== undefined;

                return (
                  <div
                    key={unit.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => handleSelect(unit)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleSelect(unit);
                      }
                    }}
                    className={`w-full px-4 py-2.5 rounded-lg text-left border transition-all flex items-center justify-between gap-3 cursor-pointer ${
                      isSelected ? 'bg-blue-50 border-blue-500' : 'border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    <Text
                      size="medium"
                      color="black"
                      className="font-inter truncate flex-1 min-w-0"
                    >
                      {unit.name}
                    </Text>
                    {canEditDelete && (
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <button
                          type="button"
                          onClick={(event) => {
                            event.stopPropagation();
                            startEdit(unit);
                          }}
                          className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          aria-label={`Edit ${unit.name}`}
                        >
                          <FaPencilAlt size={14} />
                        </button>
                        <button
                          type="button"
                          onClick={(event) => {
                            event.stopPropagation();
                            void handleDelete(unit);
                          }}
                          disabled={deletingUnitId === unit.id}
                          className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          aria-label={`Delete ${unit.name}`}
                        >
                          <MdDelete size={18} />
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="flex items-center justify-center h-[200px] p-5">
              <Text color="gray" size="small">
                {searchQuery ? 'No units found' : 'No units available'}
              </Text>
            </div>
          )}

          {editingUnit && (
            <div className="mt-4 pt-3 border-t border-gray-200">
              <Text className="font-inter text-xs text-gray-500 mb-1" color="black">
                Editing unit: {editingUnit.name}
              </Text>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={editingName}
                  onChange={(event) => setEditingName(event.target.value)}
                  className="flex-1 h-9 px-3 border border-gray-300 rounded-lg text-sm font-inter outline-none transition-colors focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
                />
                <button
                  type="button"
                  onClick={handleEditSave}
                  disabled={isSavingEdit}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSavingEdit ? 'Saving...' : 'Save'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setEditingUnit(null);
                    setEditingName('');
                  }}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm font-semibold hover:bg-gray-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {showCreate && (
            <div className="mt-auto pt-3 pb-4 border-t border-gray-200">
              <button
                type="button"
                onClick={handleCreate}
                disabled={isCreating}
                className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg font-semibold transition-all hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isCreating ? 'Creating...' : `+ Create "${searchQuery.trim()}" as new Unit`}
              </button>
            </div>
          )}
        </div>
      </Modal>
    );
  }
);

ReadymadeUnitsModal.displayName = 'ReadymadeUnitsModal';

export default ReadymadeUnitsModal;
